import { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Brain, Download, CheckCircle2, AlertCircle, FileText, ListTree, Shield } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ExplainabilityViewer({ query, response, context, isOpen, onClose }) {
  const [explanation, setExplanation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reportId, setReportId] = useState(null);

  const generateExplanation = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('lawliet_token');
      const res = await axios.post(
        `${API}/explainability/generate`,
        { query, response, context },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setExplanation(res.data.explanation);
      setReportId(res.data.report_id);
      toast.success("Explication générée avec succès");
    } catch (error) {
      toast.error("Erreur lors de la génération de l'explication");
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async () => {
    if (!reportId) return;

    try {
      const token = localStorage.getItem('lawliet_token');
      const response = await axios.get(
        `${API}/explainability/export/${reportId}?format=txt`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `explication_${reportId}.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("Rapport exporté");
    } catch (error) {
      toast.error("Erreur lors de l'export");
    }
  };

  const getConfidenceColor = (score) => {
    if (score >= 0.8) return "text-green-600";
    if (score >= 0.6) return "text-amber-600";
    return "text-red-600";
  };

  const getConfidenceLabel = (score) => {
    if (score >= 0.8) return "Élevée";
    if (score >= 0.6) return "Moyenne";
    return "Faible";
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-blue-900" />
            Explication du Raisonnement IA
          </DialogTitle>
          <DialogDescription>
            Détails sur le processus de réflexion et les sources consultées
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[70vh] pr-4">
          {!explanation ? (
            <div className="space-y-4">
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="pt-6">
                  <p className="text-sm text-blue-800">
                    Générez une explication détaillée du raisonnement juridique utilisé pour cette réponse.
                    L'IA analysera les étapes de réflexion, les sources consultées et le niveau de confiance.
                  </p>
                </CardContent>
              </Card>

              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-sm font-semibold text-slate-700 mb-2">Question posée :</p>
                <p className="text-sm text-slate-600">{query}</p>
              </div>

              <Button
                onClick={generateExplanation}
                disabled={loading}
                className="w-full bg-blue-600"
              >
                {loading ? (
                  <>
                    <Brain className="h-4 w-4 mr-2 animate-pulse" />
                    Génération en cours... (10-15 secondes)
                  </>
                ) : (
                  <>
                    <Brain className="h-4 w-4 mr-2" />
                    Générer l'explication
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Confidence Score */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between text-lg">
                    <span className="flex items-center gap-2">
                      <Shield className="h-5 w-5" />
                      Score de Confiance
                    </span>
                    <Badge className={getConfidenceColor(explanation.confidence_score)}>
                      {(explanation.confidence_score * 100).toFixed(0)}% - {getConfidenceLabel(explanation.confidence_score)}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Progress value={explanation.confidence_score * 100} className="h-2" />
                </CardContent>
              </Card>

              {/* Methodology */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ListTree className="h-5 w-5" />
                    Méthodologie Appliquée
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-700">{explanation.methodology}</p>
                </CardContent>
              </Card>

              {/* Reasoning Steps */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Étapes de Raisonnement</CardTitle>
                  <CardDescription>Processus d'analyse détaillé</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {explanation.reasoning_steps && explanation.reasoning_steps.length > 0 ? (
                    explanation.reasoning_steps.map((step, index) => (
                      <div key={index} className="border-l-4 border-l-blue-500 pl-4 py-2">
                        <div className="flex items-start gap-3">
                          <div className="bg-blue-100 text-blue-900 rounded-full w-8 h-8 flex items-center justify-center font-semibold text-sm flex-shrink-0">
                            {step.step_number}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-slate-900 mb-1">{step.title}</h4>
                            <p className="text-sm text-slate-600">{step.description}</p>
                            
                            {step.sources_used && step.sources_used.length > 0 && (
                              <div className="mt-2 bg-slate-50 p-2 rounded text-xs">
                                <span className="font-semibold">Sources : </span>
                                {step.sources_used.join(", ")}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-4">
                      Analyse étape par étape en cours...
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Sources Consulted */}
              {explanation.sources_consulted && explanation.sources_consulted.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Sources Consultées
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {explanation.sources_consulted.map((source, index) => (
                      <div key={index} className="bg-slate-50 p-3 rounded">
                        <div className="flex items-start justify-between mb-1">
                          <p className="font-semibold text-sm">{source.title}</p>
                          <Badge variant="outline" className="text-xs">
                            {(source.relevance_score * 100).toFixed(0)}% pertinent
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-500 mb-1">Type : {source.type}</p>
                        {source.excerpt && (
                          <p className="text-xs text-slate-600 italic mt-2">
                            "{source.excerpt}"
                          </p>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Limitations */}
              <Card className="bg-amber-50 border-amber-200">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-amber-600" />
                    Limitations et Précautions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {explanation.limitations && explanation.limitations.length > 0 ? (
                      explanation.limitations.map((limitation, index) => (
                        <li key={index} className="text-sm text-amber-800 flex items-start gap-2">
                          <span className="text-amber-600 font-bold">•</span>
                          <span>{limitation}</span>
                        </li>
                      ))
                    ) : (
                      <li className="text-sm text-amber-800">
                        Cette analyse est fournie à titre informatif. Un avis juridique complémentaire peut être nécessaire.
                      </li>
                    )}
                  </ul>
                </CardContent>
              </Card>

              <Separator />

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  onClick={exportReport}
                  variant="outline"
                  className="flex-1"
                  disabled={!reportId}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Exporter le rapport
                </Button>
                <Button onClick={onClose} className="flex-1 bg-blue-600">
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Terminé
                </Button>
              </div>
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
